/**
 * Watchman Theme in material-ui.
 *
 * Tree of available customizations:
 * https://material-ui.com/customization/default-theme/
 */
import { createMuiTheme } from '@material-ui/core';
import { FONT_WEIGHT_BOLD, FONT_WEIGHT_NORMAL, FONT_WEIGHT_SEMI, FONT_SIZE_BODY } from './Material';

export const COLOR_WHITE = '#ffffff';
export const COLOR_BLACK = '#000000';

// Special one off colour to get opacity setting into background colour only. Opacity property would set for color as well,
export const BACKGROUND_COLOUR_WITH_TRANSPARENCY = '#0A648F80';

export const COLOR_GREY = {
    dark: '#747B84',
    main: '#A7B4BF',
    light: '#EFEFEF',
};

// See palette options - https://material-ui.com/customization/palette/
const palette = {
    primary: {
        dark: '#0A648F',
        main: '#7BCEF6',
        light: '#D0EDFB',
        contrastText: COLOR_WHITE,
    },
    secondary: {
        main: '#D0EDFB',
        dark: '#0A648F',
        contrastText: '#7BCEF6',
    },
    info: {
        main: '#E8FBFC',
        contrastText: '#117A9B',
    },
    warning: {
        main: '#117A9B',
        contrastText: COLOR_WHITE,
    },
    error: {
        main: '#D5005D',
    },
    success: {
        main: '#0A648F',
        contrastText: COLOR_WHITE,
    },
    background: {
        default: '#F0F8FC',
    },
    action: {
        main: '#D0EDFB',
        contrastText: '#494372',
        disabledContrastText: '#888ca9',
        disabled: '#f5f4fd',
    },
    text: {
        primary: '#0A648F',
    },
};

// Font sizes - https://material-ui.com/customization/typography/
const typography = {
    fontFamily: ['Avenir Next', 'Nunito Sans'].join(','),
    h1: {
        fontSize: '30px',
        fontWeight: FONT_WEIGHT_BOLD,
    },
    h2: {
        fontSize: '20px',
        fontWeight: FONT_WEIGHT_BOLD,
        lineHeight: '27px',
        letterSpacing: '0.24px',
    },
    h3: {
        fontSize: '18px',
        fontWeight: FONT_WEIGHT_BOLD,
    },
    h4: {
        fontWeight: FONT_WEIGHT_BOLD,
        fontSize: '14px',
    },
    body1: {
        fontSize: FONT_SIZE_BODY,
        fontWeight: FONT_WEIGHT_NORMAL,
    },
    body2: {
        fontSize: FONT_SIZE_BODY,
        fontWeight: FONT_WEIGHT_SEMI,
    },
    button: {
        textTransform: 'none', // Disables the default material all caps of button text
        fontSize: FONT_SIZE_BODY,
        fontWeight: FONT_WEIGHT_BOLD,
    },
    caption: {
        fontSize: '14px',
    },
    subtitle1: {
        fontSize: FONT_SIZE_BODY,
        fontWeight: FONT_WEIGHT_BOLD,
    },
};

export const PROGRESS_CONTAINER_COLOUR = palette.background.default;
export const PROGRESS_BAR_COLOUR = palette.primary.main;
export const CHAT_ERROR_COLOUR = '#EB5160';

// Breakpoint customization
// https://material-ui.com/customization/breakpoints/#custom-breakpoints
const breakpoints = {
    values: {
        sm: 0,
        md: 360,
        lg: 830,
    },
};

// Default props and css overrides for controls
// https://material-ui.com/customization/globals/#default-props
const MuiButtonProps = {
    variant: 'contained',
    color: 'primary',
};
const MuiButtonOverrides = {
    containedPrimary: {
        '&:disabled': {
            color: '#ffffff',
            background: '#D0EDFB',
        },
        height: '45px',
        boxShadow: 'none',
    },
    outlinedPrimary: {
        height: '45px',
    },
    root: {
        borderRadius: '5px',
    },
};

const MuiTypographyOverrides = {
    root: { color: palette.primary.dark },
};

const MuiTextFieldProps = {
    variant: 'outlined',
};
const MuiOutlinedInputOverrides = {
    root: {
        height: '45px',
    },
    input: {
        fontWeight: FONT_WEIGHT_SEMI,
        fontSize: FONT_SIZE_BODY,
        color: palette.primary.dark,
    },
};

// Bag of colors that the app uses that might differ from theme to theme.
export const Colors = {
    COLOR_WHITE,
    COLOR_GREY,
    COLOR_BLACK,
    BACKGROUND_COLOUR_WITH_TRANSPARENCY,
    PROGRESS_CONTAINER_COLOUR,
    PROGRESS_BAR_COLOUR,
    CHAT_ERROR_COLOUR,
};

/**
 * The default Watchman theme.
 */
export const WatchmanTheme = createMuiTheme({
    palette,
    typography,
    breakpoints,
    props: {
        MuiButton: MuiButtonProps,
        MuiTextField: MuiTextFieldProps,
    },
    overrides: {
        MuiButton: MuiButtonOverrides,
        MuiOutlinedInput: MuiOutlinedInputOverrides,
        MuiTypography: MuiTypographyOverrides,
    },
});
